import React, { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import { getErrorMessage } from '../utils/errorHandler';

const AuthContext = createContext(null);

// BACKEND_URL detection: env var > localStorage cache > network detection > hardcoded default
// In production (Docker), nginx proxies /api/* to the backend, so we use relative URLs.
// In development, we use the env var or the hardcoded fallback to localhost:5163.
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const DEFAULT_BACKEND_URL = ENV_BACKEND_URL || (isLocalhost ? 'http://localhost:5163' : '');
const BACKEND_URL = localStorage.getItem('backend_url') || DEFAULT_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Async fetch to update the backend URL from the server config endpoint
const syncBackendConfig = async () => {
  try {
    const baseUrl = BACKEND_URL || '';
    const resp = await fetch(`${baseUrl}/api/config`);
    const config = await resp.json();
    if (config.backend_url) {
      localStorage.setItem('backend_url', config.backend_url);
    }
  } catch {
    // Silent - use default/cached URL
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  
  // Sync backend config on mount (runs once, updates localStorage for next load)
  useEffect(() => {
    syncBackendConfig();
  }, []);

  // Función para limpiar sesión y redirigir al login
  const forceLogout = () => {
    console.warn('Sesión inválida detectada - forzando logout');
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setUser(null);
    // Forzar redirección al login si no estamos ya ahí
    if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
      window.location.href = '/login';
    }
  };


  // Refresco proactivo: renueva el token 10 minutos antes de expirar
  useEffect(() => {
    const proactiveRefresh = async () => {
      const storedToken = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refresh_token');
      if (!storedToken || !refreshToken) return;
      try {
        const payload = JSON.parse(atob(storedToken.split('.')[1]));
        const expiresInMs = (payload.exp * 1000) - Date.now();
        if (expiresInMs < 10 * 60 * 1000) {
          const response = await axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken });
          const newToken = response.data.token;
          localStorage.setItem('token', newToken);
          setToken(newToken);
        }
      } catch {
        // Silencioso - el interceptor maneja el 401 si falla
      }
    };
    const interval = setInterval(proactiveRefresh, 5 * 60 * 1000);
    proactiveRefresh();
    return () => clearInterval(interval);
  }, []);

  // Interceptor de Axios para refresh automático
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config;
        
        // Si la petición al endpoint de refresh falla, hacer logout directo
        if (originalRequest?.url?.includes('/auth/refresh') && error.response?.status === 401) {
          forceLogout();
          return Promise.reject(error);
        }
        
        // Si es 401 y no hemos intentado refresh aún
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          const refreshToken = localStorage.getItem('refresh_token');
          if (refreshToken) {
            try {
              // Intentar refrescar el token
              const response = await axios.post(`${API}/auth/refresh`, {
                refresh_token: refreshToken
              });
              
              const newToken = response.data.token;
              localStorage.setItem('token', newToken);
              setToken(newToken);
              
              // Reintentar la petición original con el nuevo token
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return axios(originalRequest);
            } catch (refreshError) {
              // Si falla el refresh, forzar logout completo
              console.error('Refresh token expired or invalid');
              forceLogout();
              return Promise.reject(refreshError);
            }
          } else {
            // No hay refresh token, forzar logout
            forceLogout();
          }
        }
        
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    const loadUser = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(response.data);
          setToken(storedToken);
        } catch (error) {
          console.error('Failed to load user:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('refresh_token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    loadUser();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { user, token, refresh_token } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('refresh_token', refresh_token);
      setToken(token);
      setUser(user);
      return { success: true, user };
    } catch (error) {
      return { 
        success: false, 
        error: getErrorMessage(error, 'Login failed') 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      const { user, token } = response.data;
      localStorage.setItem('token', token);
      setToken(token);
      setUser(user);
      return { success: true, user };
    } catch (error) {
      return { 
        success: false, 
        error: getErrorMessage(error, 'Registration failed') 
      };
    }
  };

  const logout = () => {
    setIsLoggingOut(true);
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setUser(null);
    // Reset logging out flag after a short delay
    setTimeout(() => setIsLoggingOut(false), 100);
  };

  const getAuthHeaders = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token,
      loading,
      isLoggingOut,
      login, 
      register, 
      logout,
      getAuthHeaders,
      updateUser 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Cache-busting version: cambia en cada carga de página para forzar imágenes frescas
const _imageVersion = Date.now();

// Helper para construir URLs de imágenes completas
export const getImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  // Si ya es una URL completa (http/https) o base64, retornarla tal cual
  if (imageUrl.startsWith('http') || imageUrl.startsWith('data:')) {
    return imageUrl;
  }
  // Si es una ruta relativa, agregar el backend URL con cache-busting
  const separator = imageUrl.includes('?') ? '&' : '?';
  return `${BACKEND_URL}${imageUrl}${separator}v=${_imageVersion}`;
};

export { API, BACKEND_URL };
